
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Trash2, Loader2, GraduationCap } from 'lucide-react';
import ImageUpload from './ImageUpload';

interface EducationItem {
    id: string;
    degree: string;
    institution: string;
    location: string;
    startYear: number;
    endYear: number;
    gpa?: string;
    achievements: string[];
    logo?: string;
    logoAlt?: string;
}

export default function EducationManager() {
    const [items, setItems] = useState<EducationItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);

    const [newItem, setNewItem] = useState<Partial<EducationItem>>({
        degree: '',
        institution: '',
        location: '',
        startYear: new Date().getFullYear(),
        endYear: new Date().getFullYear(),
        gpa: '',
        achievements: [],
        logo: ''
    });

    const [achievementsInput, setAchievementsInput] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const { data, error } = await supabase
                .from('site_content')
                .select('content')
                .eq('section_key', 'about_education')
                .maybeSingle();

            if (error) throw error;
            if (data && data.content) {
                setItems(data.content);
            }
        } catch (err) {
            console.error('Error fetching education:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (updatedList: EducationItem[]) => {
        setSaving(true);
        try {
            const { error } = await supabase
                .from('site_content')
                .upsert({
                    section_key: 'about_education',
                    content: updatedList,
                    updated_at: new Date().toISOString()
                });

            if (error) throw error;
            setItems(updatedList);
            setIsFormOpen(false);
            setNewItem({ degree: '', institution: '', location: '', startYear: new Date().getFullYear(), endYear: new Date().getFullYear(), gpa: '', achievements: [], logo: '' });
            setAchievementsInput('');
        } catch (err) {
            console.error('Error saving education:', err);
            alert('Failed to save education');
        } finally {
            setSaving(false);
        }
    };

    const addItem = () => {
        if (!newItem.degree || !newItem.institution) return;

        const itemToAdd: EducationItem = {
            id: Math.random().toString(36).substr(2, 9),
            degree: newItem.degree!,
            institution: newItem.institution!,
            location: newItem.location || '',
            startYear: newItem.startYear || new Date().getFullYear(),
            endYear: newItem.endYear || new Date().getFullYear(),
            gpa: newItem.gpa,
            achievements: achievementsInput.split('\n').map(a => a.trim()).filter(a => a),
            logo: newItem.logo,
            logoAlt: `${newItem.institution} logo`
        };

        const updated = [...items, itemToAdd];
        handleSave(updated);
    };

    const deleteItem = (id: string) => {
        if (!confirm('Delete this education entry?')) return;
        const updated = items.filter(i => i.id !== id);
        handleSave(updated);
    };

    if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-blue-500" /></div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-white">Education</h2>
                <button
                    onClick={() => setIsFormOpen(!isFormOpen)}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                    {isFormOpen ? 'Cancel' : <><Plus size={18} /> Add Education</>}
                </button>
            </div>

            {isFormOpen && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-400 mb-1">Degree</label>
                            <input
                                type="text"
                                value={newItem.degree}
                                onChange={(e) => setNewItem({ ...newItem, degree: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                placeholder="e.g., Bachelor of Science in Computer Science"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Institution</label>
                            <input
                                type="text"
                                value={newItem.institution}
                                onChange={(e) => setNewItem({ ...newItem, institution: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Location</label>
                            <input
                                type="text"
                                value={newItem.location}
                                onChange={(e) => setNewItem({ ...newItem, location: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Start Year</label>
                            <input
                                type="number"
                                value={newItem.startYear}
                                onChange={(e) => setNewItem({ ...newItem, startYear: parseInt(e.target.value) })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">End Year</label>
                            <input
                                type="number"
                                value={newItem.endYear}
                                onChange={(e) => setNewItem({ ...newItem, endYear: parseInt(e.target.value) })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-400 mb-1">GPA (optional)</label>
                            <input
                                type="text"
                                value={newItem.gpa}
                                onChange={(e) => setNewItem({ ...newItem, gpa: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                placeholder="e.g., 3.8/4.0"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Achievements (one per line)</label>
                        <textarea
                            rows={4}
                            value={achievementsInput}
                            onChange={(e) => setAchievementsInput(e.target.value)}
                            className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            placeholder="Dean's List&#10;Graduated with Honors&#10;etc."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Institution Logo</label>
                        <div className="max-w-xs">
                            <ImageUpload
                                value={newItem.logo || null}
                                onChange={(url) => setNewItem({ ...newItem, logo: url })}
                                folder="education"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end pt-4">
                        <button
                            onClick={addItem}
                            disabled={saving || !newItem.degree || !newItem.institution}
                            className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                        >
                            {saving ? <Loader2 className="animate-spin h-5 w-5" /> : 'Save Education'}
                        </button>
                    </div>
                </div>
            )}

            <div className="space-y-4">
                {items.map((item) => (
                    <div key={item.id} className="bg-gray-900 border border-gray-800 rounded-xl p-6 flex gap-6 group hover:border-gray-700 transition-colors relative">
                        <button
                            onClick={() => deleteItem(item.id)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-2"
                        >
                            <Trash2 size={20} />
                        </button>

                        <div className="flex-shrink-0">
                            {item.logo ? (
                                <img src={item.logo} alt={item.institution} className="h-16 w-16 rounded-lg object-contain bg-white p-2" />
                            ) : (
                                <div className="h-16 w-16 rounded-lg bg-gray-800 flex items-center justify-center text-gray-500">
                                    <GraduationCap size={32} />
                                </div>
                            )}
                        </div>

                        <div className="flex-grow">
                            <h3 className="text-lg font-semibold text-white">{item.degree}</h3>
                            <p className="text-blue-400 font-medium">{item.institution}</p>
                            <p className="text-sm text-gray-500 mt-1">{item.location} • {item.startYear} - {item.endYear}</p>
                            {item.gpa && <p className="text-sm text-gray-400 mt-1">GPA: {item.gpa}</p>}
                            {item.achievements && item.achievements.length > 0 && (
                                <ul className="mt-3 space-y-1">
                                    {item.achievements.map((achievement, i) => (
                                        <li key={i} className="text-sm text-gray-300 flex items-start">
                                            <span className="text-blue-400 mr-2">•</span>
                                            {achievement}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                ))}

                {items.length === 0 && !loading && (
                    <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-800 rounded-xl">
                        No education entries added yet.
                    </div>
                )}
            </div>
        </div>
    );
}
